'use client';
import React from 'react';
import { Card, Input, Button, InputPassword, Select, Checkbox, Heading } from '@ui';
import { withOptionalId } from '@lib/withOptionalId';
import { Section, Container } from '@layout';
import MarkupSnippet, { markupCopyLabels } from '../markup-snippet/MarkupSnippet';
import useInlineValidation from '@/hooks/useInlineValidation';

type Props = {
  sectionID?: string;
  classes?: string;
  containerId?: string;
  containerClasses?: string;
  cardClassName?: string;
  isSpanish?: boolean;
};

const InlineFormValidation = ({
  isSpanish = false,
  sectionID,
  classes = '',
  containerId,
  containerClasses,
  cardClassName,
}: Props) => {
  useInlineValidation('demoValidate');
  const t = (en: string, es: string) => (isSpanish ? es : en);
  // To enable stricter password rules, uncomment:
  // import { strictPasswordRules } from '@lib/passwordRules';
  // useInlineValidation('demoValidate', { passwordRules: strictPasswordRules });

  return (
    <Section {...withOptionalId(sectionID)} className={classes}>
      <Container containerId={containerId} containerClasses={containerClasses}>
        <Heading
          as='h3'
          title={t('Inline Form Validation', 'Validación de Formularios en Línea')}
          description={t(
            'Client-side rules with inline messages, an error summary, and accessible states.',
            'Reglas del lado del cliente con mensajes en línea, un resumen de errores y estados accesibles.'
          )}
        />

        <Card className={cardClassName}>
          <form id='demoValidate' className='form'>
            <div
              className='form-summary'
              id='formSummary'
              role='alert'
              aria-live='polite'
              hidden
            ></div>
            <div className='grid-3 form-grid'>
              <fieldset>
                <Input
                  id='vName'
                  name='vName'
                  label={
                    <>
                      {t('Full Name', 'Nombre completo')} <b className='req'>*</b>
                    </>
                  }
                  placeholder={t('Name Lastname ', 'Nombre Apellido ')}
                  hint=' '
                  hintId='err-name'
                  hintHidden
                  hintClassName='error'
                />
              </fieldset>
              <fieldset>
                <Input
                  id='vEmail'
                  name='vEmail'
                  type='email'
                  label={
                    <>
                      {t('Email', 'Correo')} <b className='req'>*</b>
                    </>
                  }
                  placeholder='email@domain.com'
                  hint=' '
                  hintId='err-email'
                  hintHidden
                  hintClassName='error'
                />
              </fieldset>
              <fieldset>
                <InputPassword
                  id='vPass'
                  name='vPass'
                  label={
                    <>
                      {t('Password', 'Contraseña')} <b className='req'>*</b>
                    </>
                  }
                  hint=' '
                  hintId='err-pass'
                  hintHidden
                  hintClassName='error'
                />
              </fieldset>
              <fieldset>
                <InputPassword
                  id='vPass2'
                  name='vPass2'
                  label={
                    <>
                      {t('Confirm Password', 'Confirmar contraseña')} <b className='req'>*</b>
                    </>
                  }
                  hint=' '
                  hintId='err-pass2'
                  hintHidden
                  hintClassName='error'
                />
              </fieldset>
              <fieldset>
                <Input
                  id='vAge'
                  name='vAge'
                  type='number'
                  label={t('Age (optional)', 'Edad (opcional)')}
                  placeholder='18–120'
                  hint=' '
                  hintId='err-age'
                  hintHidden
                  hintClassName='error'
                />
              </fieldset>
              <fieldset>
                <Select
                  id='vCountry'
                  name='vCountry'
                  label={
                    <>
                      {t('Country', 'País')} <b className='req'>*</b>
                    </>
                  }
                  options={[
                    {
                      value: '',
                      label: t('Select Country', 'Selecciona un país'),
                      placeholder: true,
                    },
                    { value: 'US', label: t('United States', 'Estados Unidos') },
                    { value: 'CA', label: t('Canada', 'Canadá') },
                    { value: 'GB', label: t('United Kingdom', 'Reino Unido') },
                    { value: 'AU', label: t('Australia', 'Australia') },
                  ]}
                  hint=' '
                  hintId='err-country'
                  hintHidden
                  hintClassName='error'
                />
              </fieldset>
              <fieldset className='field-col row'>
                <Checkbox
                  id='vTerms'
                  name='vTerms'
                  type='checkbox'
                  label={
                    <>
                      {t('I agree to the terms.', 'Acepto los términos.')} <b className='req'>*</b>
                    </>
                  }
                  hint=' '
                  hintId='err-terms'
                  hintHidden
                  hintClassName='error'
                />
              </fieldset>
            </div>
            <div className='row btn-row mt-16' style={{ justifyContent: 'flex-end' }}>
              <Button type='reset' className='btn-ghost' label={t('Reset', 'Reiniciar')} />
              <Button type='submit' className='btn-primary' label={t('Submit', 'Enviar')} />
            </div>
          </form>
        </Card>
      <MarkupSnippet
          {...markupCopyLabels(isSpanish)}
          code={`<fieldset class="fieldset">
  <label for="email">Work email</label>
  <input id="email" class="is-invalid" type="email" value="not-an-email" />
  <small class="hint error">Enter a valid email address.</small>
</fieldset>`}
        />
      </Container>
    </Section>
  );
};

export default InlineFormValidation;
